import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import {
  DataTable,
  DrawerHead,
  ErrorState,
  Headline,
  KernelError,
  Loading,
  Provenance,
  ReachableBy,
  RecordGraph,
  Refusal,
  controlGet,
} from "@lopie/kernel-ui";

import DataFlow from "../components/DataFlow.jsx";
import { withoutSandboxes } from "./sandbox.js";
import { TypeMap } from "@lopie/kernel-ui";

const PAGE_SIZES = [25, 50, 100, 200];

/**
 * Browse the graph.
 *
 * Everything structural here comes from `GET /schema` — which types exist,
 * what each is called, which path it lives at, and which fields it has. There
 * is deliberately no map of collection to type in this file: the reader app
 * (LO-15) started with one and it was already wrong, because `/schema` had
 * carried `collection` per type the whole time.
 *
 * The test that keeps it honest: a type added to the kernel must appear here
 * with no change to this file.
 */
/**
 * `display_name` is what the kernel calls it. "Display name" is what it is.
 *
 * A generic transform and not a map, for the reason every other list in this
 * console is generated: a table of pretty names for fields would be a second
 * copy of the schema, out of date the first time somebody adds one. Underscores
 * become spaces and the first letter is raised, which is the whole rule.
 *
 * It deliberately does not touch the rest of the casing. `emails` stays
 * `Emails`; an acronym somebody puts in a field name comes through as they
 * wrote it, because this file has no way to tell one from a typo.
 */
function humanise(name) {
  const words = String(name).replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export default function Objects({ get, onEffective, token }) {
  const [schema, setSchema] = useState(null);
  const [collection, setCollection] = useState(null);
  const [mapTab, setMapTab] = useState(0);
  const [tagged, setTagged] = useState("");
  const [sort, setSort] = useState("asc");
  const [limit, setLimit] = useState(50);

  const [page, setPage] = useState(null);
  const [stack, setStack] = useState([]); // cursors behind us
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null);
  const [selected, setSelected] = useState(null);
  // The one-hop ring around the selected record, fetched when it opens
  // rather than for every row in the table.
  const [graph, setGraph] = useState(null);
  const [graphFailure, setGraphFailure] = useState(null);
  const wanted = useRef(null);

  // The tag options a caller may filter by. Not a literal list: filtering to
  // something the manifest omits is a 403, and offering it in a dropdown is
  // offering a mistake.
  /** Open a record and fetch its one-hop ring.

   * Fetched on open rather than with the page: the graph endpoint reads
   * every relationship and asks every manifest, and doing that for fifty
   * rows to render a table nobody has clicked would be work for nothing.
   */
  const open = useCallback(async (row) => {
    setSelected(row);
    setGraph(null);
    setGraphFailure(null);
    // Which record this request is for. Two clicks in quick succession
    // resolve in whatever order the network decides, and the first version
    // of this checked "has nothing arrived yet", which is true of exactly
    // the case it was meant to reject — so a slow response for record A
    // landed beside record B's provenance.
    wanted.current = row.id;
    try {
      const body = await get(`/${collection}/${row.id}/graph`);
      if (wanted.current !== row.id) return;
      setGraph(body);
    } catch (e) {
      if (wanted.current !== row.id) return;
      setGraphFailure(e);
    }
  }, [get, collection]);

  const close = useCallback(() => {
    wanted.current = null;
    setSelected(null);
    setGraph(null);
    setGraphFailure(null);
  }, []);

  const [tagOptions, setTagOptions] = useState([]);
  const declared = useRef(null);

  useEffect(() => {
    let live = true;
    get("/schema")
      .then((s) => {
        if (!live) return;
        setSchema(s);
        setCollection((c) => c || s.types?.[0]?.collection || null);
      })
      .catch((e) => live && setFailure(e))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [get]);

  // What connects and ingests what: best-effort and separate from the read
  // above. `/apps` and `/connectors` are already permitted to this console
  // (the Apps and Connectors screens read them), so nothing is widened to
  // draw this — but a refusal on either one should not take the type
  // browser above it down too, so it fails into "this section does not
  // render" rather than into `failure`.
  //
  // `/apps` lists every approved manifest whether or not anything was ever
  // provisioned under it — Portal's own `/instances` docstring makes the
  // same point about the console appearing in its own output. A custom
  // app's manifest survives its instance being removed, so `app-sms-client`
  // from a torn-down test kept showing here as though it were still reading
  // data. Only the `app-*` namespace is provisioned/removable this way
  // (`Requests.jsx`'s own `titleOf`: the prefix exists so the provisioner
  // can be granted `app-*` once) — a platform app like `console` or
  // `agent-os` has
  // no control-plane instance to check and is never "inactive" in this
  // sense, so it is kept regardless.
  const [flow, setFlow] = useState(null);
  useEffect(() => {
    let live = true;
    Promise.all([
      get("/apps"),
      get("/connectors"),
      token
        ? controlGet(token)("/instances").catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([appsBody, connBody, deployedBody]) => {
        if (!live) return;
        const active = new Set(
          withoutSandboxes(deployedBody?.instances)
            .filter((d) => d.desiredState !== "absent")
            .map((d) => d.app),
        );
        const apps = (appsBody.apps || [])
          .filter((a) => !a.unparseable)
          .filter((a) => {
            const appType = a.app_type || a.app || "";
            return !appType.startsWith("app-") || active.has(appType);
          });
        setFlow({ apps, writers: connBody.writers || [] });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [get, token]);

  const load = useCallback(async () => {
    if (!collection) return;
    setLoading(true);
    setFailure(null);
    try {
      const body = await get(`/${collection}`, {
        limit,
        sort,
        tagged,
        cursor,
      });
      setPage(body);
      const permitted = body.permitted_tags || [];
      setTagOptions(permitted);
      declared.current = permitted;
      onEffective({ permitted, declared: permitted });
    } catch (e) {
      setPage(null);
      setFailure(e);
      // A manifest refusal still tells us what the app declares, and that is
      // exactly the moment the strip is most worth reading.
      if (e instanceof KernelError && e.isManifest) {
        onEffective({ permitted: [], declared: declared.current });
      }
    } finally {
      setLoading(false);
    }
  }, [get, collection, limit, sort, tagged, cursor, onEffective]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = (fn) => (event) => {
    setStack([]);
    setCursor(null);
    fn(event.target.value);
  };

  // Same reset `reset(setCollection)` does, for the one caller that already
  // has a value rather than an event — a click on `TypeMap`'s own node.
  const selectType = useCallback((next) => {
    setStack([]);
    setCursor(null);
    setCollection(next);
  }, []);

  const type = schema?.types?.find((t) => t.collection === collection);
  const fields = type?.fields || [];

  // Columns are derived, never written out. `/schema` carries `collection`,
  // `fields`, `label` and `minor_units` per type, so a type added to the
  // kernel — or a field added to one — appears here with no change to this
  // file. That is the rule, and this is the shape of obeying it.
  //
  // ## The name first and the id last
  //
  // `Kernel id` was the leading column: twenty-six characters of ULID, in
  // monospace, before anybody's name. It is the right thing to lead with if
  // you are debugging a row and the wrong thing for everybody else, and
  // everybody else is most people who open this screen.
  //
  // Which field is the name is not a guess: `/schema` carries `label` per
  // type — `display_name` for a Person, and whatever the type says for
  // anything added later. Same rule as everywhere else here: the kernel knows
  // and the screen asks rather than keeping its own list.
  //
  // The id stays, at the end, because it is the string you paste into a
  // support conversation and the one thing on the row that is unambiguous.
  const columns = useMemo(() => {
    const named = fields.filter((f) => f.name === type?.label);
    const rest = fields.filter((f) => f.name !== type?.label);
    const column = (f) => ({
      key: f.name,
      label: humanise(f.name),
      // `minor_units` is what makes the alignment a fact rather than a
      // guess, and DataTable turns right-alignment into `tabular-nums` so
      // the digits in a money column line up without anyone remembering to
      // ask for it.
      align: f.minor_units ? "right" : "left",
      render: (row) => cell(row, f),
    });

    return [
      ...named.map(column),
      {
        key: "tags",
        label: "Tags",
        render: (row) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {(row.tags || []).length ? (
              row.tags.map((t) => (
                <Chip key={t} size="small" label={t} variant="outlined" />
              ))
            ) : (
              <Typography variant="caption" color="text.secondary">
                untagged
              </Typography>
            )}
          </Stack>
        ),
      },
      ...rest.map(column),
      { key: "id", label: "Kernel id", mono: true },
    ];
  }, [fields, type]);

  // Two ways of looking at the same brain, tabbed rather than stacked —
  // each is a full diagram in its own right and reading them in sequence on
  // one screen was scrolling past the first to reach the second nine times
  // out of ten. Built conditionally on what actually loaded: a tab for a
  // read that failed is a tab with nothing behind it.
  const mapTabs = [
    schema?.types?.length ? {
      key: "types",
      label: "Types",
      body: (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Every type in the schema and the relationships declared between
            them — click one to browse it below.
          </Typography>
          <TypeMap types={schema.types} selected={collection} onSelect={selectType} />
        </>
      ),
    } : null,
    flow ? {
      key: "flow",
      label: "Connects & ingests",
      body: (
        <DataFlow
          apps={flow.apps}
          writers={flow.writers}
          knownTypes={(schema?.types || []).map((t) => t.type)}
          onSelectType={(typeName) => {
            const match = schema?.types?.find((t) => t.type === typeName);
            if (match) selectType(match.collection);
          }}
        />
      ),
    } : null,
  ].filter(Boolean);

  if (!schema && loading) return <Loading label="Reading the schema" />;

  return (
    <Stack spacing={2}>
      {/* The count is of this page, and the caption says so rather than
          leaving a reader to assume a total. There is no total here on
          purpose: `/counts` is a separate, bounded read, and putting its
          number under this heading would be quietly answering a different
          question with the same-looking figure. */}
      <Headline
        eyebrow={type ? type.type : "Loading"}
        figure={page ? (page.items || []).length.toLocaleString()
          : failure ? (failure instanceof KernelError && failure.isManifest ? "Not declared" : "Refused")
          : "—"}
        caption={page ? `on this page${page.has_more ? ", of more" : ""}`
          : failure ? (failure instanceof KernelError && failure.isManifest
            ? "this console's manifest does not read this type"
            : "this read was refused")
          : "on this page"}
        basis={
          type?.description
            ? `${type.description} This is what one read returned under your own permission — never a count of the collection, which costs a full read of the partition.`
            : undefined
        }
      />

      {mapTabs.length ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Tabs
            value={Math.min(mapTab, mapTabs.length - 1)}
            onChange={(_, v) => setMapTab(v)}
            sx={{ minHeight: 36, mb: 1.5 }}
          >
            {mapTabs.map((t) => (
              <Tab key={t.key} label={t.label} sx={{ minHeight: 36, py: 0.5 }} />
            ))}
          </Tabs>
          {mapTabs[Math.min(mapTab, mapTabs.length - 1)].body}
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            select
            size="small"
            label="Type"
            value={collection || ""}
            onChange={reset(setCollection)}
            sx={{ minWidth: 180 }}
          >
            {(schema?.types || []).map((t) => (
              <MenuItem key={t.collection} value={t.collection}>
                {t.type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Tagged"
            value={tagged}
            onChange={reset(setTagged)}
            sx={{ minWidth: 180 }}
            slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
          >
            <MenuItem value="">Everything this app declares</MenuItem>
            {tagOptions.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Order"
            value={sort}
            onChange={reset(setSort)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="asc">Oldest first</MenuItem>
            <MenuItem value="desc">Newest first</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Per page"
            value={limit}
            onChange={reset((v) => setLimit(Number(v)))}
            sx={{ minWidth: 110 }}
          >
            {PAGE_SIZES.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            disabled={!stack.length || loading}
            onClick={() => {
              const back = [...stack];
              setCursor(back.pop() ?? null);
              setStack(back);
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            disabled={!page?.cursor || loading}
            onClick={() => {
              setStack([...stack, cursor]);
              setCursor(page.cursor);
            }}
          >
            Next
          </Button>
        </Stack>

        {type?.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {type.description}
          </Typography>
        ) : null}
      </Paper>

      {failure ? <Refusal failure={failure} actor="This console" onRetry={load} /> : null}

      {page ? (
        <DataTable
          columns={columns}
          rows={page.items}
          onRowClick={open}
          stickyHeader
          tagged={tagged}
          emptyTitle="No rows"
          caption={
            <>
              {page.count} on this page
              {page.has_more ? ", more available" : ", last page"} · page{" "}
              {stack.length + 1}
              {page.tagged ? ` · filtered to ${page.tagged}` : ""}.{" "}
              {/* Said every time, because a count that sometimes means the
                  page and sometimes means the collection is a count nobody
                  trusts. */}
              The count is this page, never the collection — a total costs a
              full read of the partition.
            </>
          }
        />
      ) : null}

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={close}
        slotProps={{
          paper: { sx: { width: { xs: "100%", sm: 560, md: 820 } } },
        }}
      >
        {selected ? (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <DrawerHead
              // The record's own display name when it has one — "Gabriel
              // Assouline" rather than "Person". The type is the fallback and
              // not the first choice: a drawer headed with a type name is a
              // drawer that could be about any of two thousand rows.
              title={
                selected.fields?.display_name
                || selected.fields?.name
                || selected.display_name
                || selected.type
                || "Record"
              }
              id={[selected.type, selected.id].filter(Boolean).join(" · ")}
              onClose={close}
              lede="One record, and the three things worth knowing about it: what else it is attached to, where it came from and who put it there, and which apps could reach it as it is labelled today."
            />
            <Stack spacing={2.5}>
              {graphFailure ? (
                // Not `Refusal`: the record is open, so reading it was
                // permitted. Only its ring failed, and the row on screen is
                // still true. Amber rather than red for the same reason.
                <ErrorState
                  failure={graphFailure}
                  severity="warning"
                  title="Could not load what this connects to"
                  onRetry={() => open(selected)}
                />
              ) : null}

              {graph ? (
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
                    What this connects to
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Records linked to this one — the customer an invoice belongs
                    to, the person a case is about. Links are created by
                    whatever wrote the records, not inferred here.
                  </Typography>
                  <RecordGraph graph={graph} />
                </Box>
              ) : null}

              <Provenance row={selected} fields={fields} />

              {graph ? (
                <ReachableBy
                  apps={graph.reachable_by}
                  tags={selected.tags}
                />
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Drawer>
    </Stack>
  );
}

function cell(row, field) {
  const value = row.fields ? row.fields[field.name] : undefined;
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) return value.join(", ");
  // `minor_units` in the schema is the whole reason this is not a guess:
  // 2125 is $21.25, and a renderer that divides by 100 on a field that is not
  // minor units invents money.
  if (field.minor_units && typeof value === "number") {
    return (value / 100).toFixed(2);
  }
  return String(value);
}
